import { Box } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  EXPIRY_MS,
  FLAT_CARD_BEZEL_WIDTH,
  IMPERSONATION_USER_EVENTS,
  PHONE_BEZEL_WIDTH,
  RESIZE_HANDLE_SX,
  WARNING_THRESHOLD_MS,
} from "../constants";
import { useImpersonationMessaging } from "../hooks/useImpersonationMessaging";
import { useSessionTimer } from "../hooks/useSessionTimer";
import { useWindowInteractions } from "../hooks/useWindowInteractions";
import { useImpersonationSessions } from "../ImpersonationSessionsProvider";
import type { ImpersonationSession } from "../types";
import { FloatingWindowHeader } from "./FloatingWindowHeader";
import { ClosingOverlay, ErrorOverlay } from "./ImpersonationWindowOverlays";

interface FloatingImpersonationWindowProps {
  session: ImpersonationSession;
  /** When docked in the tab group, position/size are overridden and the header is hidden. */
  dockedLayout?: {
    position: { x: number; y: number };
    size: { width: number; height: number };
    isActiveTab: boolean;
  };
  /** Show the dock button in the header (only in tabs mode). */
  showDockButton?: boolean;
}

export function FloatingImpersonationWindow({
  session,
  dockedLayout,
  showDockButton = false,
}: FloatingImpersonationWindowProps) {
  const { adminUserId: employeeId } = useSession();
  const {
    bringToFront,
    closeSession,
    dockSession,
    minimizeSession,
    refreshCounters,
    updateSessionExpiresAt,
    updateWindowPosition,
    updateWindowSize,
  } = useImpersonationSessions();
  const elapsed = useSessionTimer(session.startedAt);

  const remainingMs = session.expiresAt ? session.expiresAt - Date.now() : EXPIRY_MS - elapsed;
  const isWarning = remainingMs <= WARNING_THRESHOLD_MS && remainingMs > 0;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeKey, setIframeKey] = useState(0);

  const sessionOrigin = useMemo(() => new URL(session.url).origin, [session.url]);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const forceClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeSession(session.id);
  }, [closeSession, session.id]);

  const handleClose = useCallback(() => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);

    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: "impersonation:end" }, sessionOrigin);
    }

    closeTimeoutRef.current = setTimeout(() => {
      forceClose();
    }, 5_000);
  }, [isClosing, sessionOrigin, forceClose]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleExpiresAtUpdate = useCallback(
    (expiresAt: number) => {
      updateSessionExpiresAt(session.id, expiresAt);
    },
    [updateSessionExpiresAt, session.id]
  );

  const { error, resetTokenState } = useImpersonationMessaging({
    employeeId,
    session,
    iframeRef,
    onSessionEnd: handleClose,
    onCleanupComplete: forceClose,
    onExpiresAtUpdate: handleExpiresAtUpdate,
  });

  const handleRefresh = useCallback(() => {
    resetTokenState();
    setIframeKey((previous) => previous + 1);
    logEvent(IMPERSONATION_USER_EVENTS.SESSION_REFRESHED, {
      employeeId,
      targetId: session.targetId,
      sessionType: session.sessionType,
      sessionId: session.id,
    });
  }, [employeeId, resetTokenState, session.targetId, session.sessionType, session.id]);

  // Watch external refresh counter (from tab refresh button)
  const externalRefreshCount = refreshCounters[session.id] ?? 0;
  const previousRefreshCount = useRef(externalRefreshCount);
  useEffect(() => {
    if (externalRefreshCount !== previousRefreshCount.current) {
      previousRefreshCount.current = externalRefreshCount;
      resetTokenState();
      setIframeKey((previous) => previous + 1);
    }
  }, [externalRefreshCount, resetTokenState]);

  const {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
  } = useWindowInteractions({
    sessionId: session.id,
    position: session.position,
    size: session.size,
    updateWindowPosition,
    updateWindowSize,
  });

  const isWorker = session.sessionType === "worker";
  const bezelWidth = isWorker ? PHONE_BEZEL_WIDTH : FLAT_CARD_BEZEL_WIDTH;

  // When docked, use the tab group's layout; when floating, use the session's own layout
  const effectivePosition = dockedLayout?.position ?? session.position;
  const effectiveSize = dockedLayout?.size ?? session.size;
  const isDocked = Boolean(dockedLayout);
  const isHidden = isDocked ? !(dockedLayout?.isActiveTab ?? false) : session.isMinimized;

  const totalWidth = effectiveSize.width + (isDocked ? 0 : bezelWidth * 2);

  return (
    <Box
      sx={{
        position: "fixed",
        left: effectivePosition.x,
        top: effectivePosition.y,
        zIndex: (theme) => {
          if (isDocked) {
            return theme.zIndex.modal + 51;
          }

          const typeOffset = session.sessionType === "worker" ? 100 : 0;
          return theme.zIndex.modal + 1 + typeOffset + session.zIndex;
        },
        display: isHidden ? "none" : "flex",
        flexDirection: "column",
        userSelect: "none",
      }}
      onPointerDown={() => {
        if (!isDocked) {
          bringToFront(session.id);
        }
      }}
    >
      {/* Header — hidden when docked (tab group has its own header) */}
      {!isDocked && (
        <FloatingWindowHeader
          session={session}
          isClosing={isClosing}
          isWarning={isWarning}
          error={error}
          remainingMs={remainingMs}
          totalWidth={totalWidth}
          handleDragStart={handleDragStart}
          handleDragMove={handleDragMove}
          handleDragEnd={handleDragEnd}
          handleRefresh={handleRefresh}
          handleClose={handleClose}
          showDockButton={showDockButton}
          dockSession={dockSession}
          minimizeSession={minimizeSession}
        />
      )}

      <Box
        sx={{
          width: isDocked ? effectiveSize.width : totalWidth,
          height: isDocked ? effectiveSize.height : effectiveSize.height + bezelWidth * 2,
          border: isDocked ? "none" : `${bezelWidth}px solid`,
          borderColor: isWorker ? "grey.800" : "grey.300",
          borderRadius: (() => {
            if (isDocked) {
              return 0;
            }

            return isWorker ? `0 0 ${40}px ${40}px` : "0 0 8px 8px";
          })(),
          overflow: "hidden",
          boxShadow: isDocked ? 0 : 24,
          backgroundColor: "background.paper",
          position: "relative",
        }}
      >
        <Box
          key={iframeKey}
          ref={iframeRef}
          component="iframe"
          src={session.url}
          sx={{
            width: "100%",
            height: "100%",
            border: "none",
            display: isClosing || error ? "none" : "block",
          }}
          title={`Impersonation - ${session.targetName}`}
        />
        {isClosing && <ClosingOverlay />}
        {!isClosing && error && <ErrorOverlay error={error} onRetry={handleRefresh} />}
        <Box
          sx={RESIZE_HANDLE_SX}
          onPointerDown={handleResizeStart}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd}
        />
      </Box>
    </Box>
  );
}
